import { useEffect, useState, useMemo } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { Input } from "@project/components/ui/input";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  Search,
  Package,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Eye,
} from "lucide-react";
import { gruhUdhyogProductService } from "@project/services/admin/gruh-udhyog-product.service";
import { gruhUdhyogCategoryService } from "@project/services/admin/gruh-udhyog-category.service";
import { gruhUdhyogVariantService } from "@project/services/admin/gruh-udhyog-variant.service";
import type {
  Product,
  ProductCategory,
  ProductPayload,
  ProductVariantMaster,
} from "@project/types/gruh-udhyog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@project/components/ui/dialog";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import { Switch } from "@project/components/ui/switch";
import { SearchableSelect } from "@project/admin/components/searchable-select";
import { DeleteConfirmDialog } from "@project/admin/components/delete-confirm-dialog";
import { TablePagination } from "@project/admin/components/table-pagination";
import { projectToast } from "@project/lib/toast";
import { getApiErrorMessage } from "@project/lib/api-error";
import { resolveMediaUrl } from "@project/lib/utils";
import {
  ProductFormVariants,
  emptyOptionRow,
  emptyVariantRow,
  resolveVariantMasterId,
  toVariantCreateItems,
  variantsFromProduct,
  type ProductOptionRow,
  type VariantFormRow,
} from "./product-form-variants";
import {
  ProductViewDialog,
  productPriceRange,
  productTotalStock,
} from "./product-view-dialog";

function productImageSrc(
  product: { image?: File | string | null; image_url?: string | null } | null
): string | null {
  if (!product) {
    return null;
  }
  if (product.image instanceof File) {
    return URL.createObjectURL(product.image);
  }
  return resolveMediaUrl(
    (typeof product.image === "string" ? product.image : null) ??
    product.image_url
  );
}
/** Dialog form state — image may be a new File before upload. */
type ProductFormState = Omit<Partial<Product>, "image"> & {
  image?: File | string | null;
};

type InitialProductState = {
  product: ProductFormState;
  variantMasterId?: number;
  variants: VariantFormRow[];
};

function areVariantsEqual(
  initialRows: VariantFormRow[],
  currentRows: VariantFormRow[],
  initialMasterId?: number,
  currentMasterId?: number
): boolean {
  if (initialMasterId !== currentMasterId) {
    return false;
  }
  const activeInitial = initialRows.filter(r => r.value.trim());
  const activeCurrent = currentRows.filter(r => r.value.trim());

  if (activeInitial.length !== activeCurrent.length) {
    return false;
  }

  const initialMap = new Map(activeInitial.map(r => [r.value.trim(), r]));
  for (const cur of activeCurrent) {
    const init = initialMap.get(cur.value.trim());
    if (!init) {
      return false;
    }
    if (
      Number(cur.price || 0) !== Number(init.price || 0) ||
      Number(cur.discounted_price || 0) !== Number(init.discounted_price || 0) ||
      Number(cur.tax || 0) !== Number(init.tax || 0) ||
      Number(cur.total_stock || 0) !== Number(init.total_stock || 0) ||
      cur.sku.trim() !== init.sku.trim() ||
      cur.is_active !== init.is_active
    ) {
      return false;
    }
  }

  return true;
}

export default function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [variantMasters, setVariantMasters] = useState<ProductVariantMaster[]>(
    []
  );
  const [selectedCatId, setSelectedCatId] = useState<number | "All">("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // CRUD States
  const [editingProduct, setEditingProduct] = useState<ProductFormState | null>(
    null
  );
  const [initialProduct, setInitialProduct] =
    useState<InitialProductState | null>(null);
  const [productOptions, setProductOptions] = useState<ProductOptionRow[]>([]);
  const [variantRows, setVariantRows] = useState<VariantFormRow[]>([
    emptyVariantRow(),
  ]);
  const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);
  const [viewingProduct, setViewingProduct] = useState<Product | null>(null);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await gruhUdhyogProductService.listPage({
        page: 1,
        page_size: 200,
      });
      setProducts(res.data ?? []);
    } catch (err) {
      projectToast.error(getApiErrorMessage(err, "Failed to load products"));
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await gruhUdhyogCategoryService.listPage({
        page: 1,
        page_size: 200,
        status: "active",
      });
      setCategories(res.data ?? []);
    } catch (err) {
      console.error("Failed to load categories", err);
    }
  };

  const fetchVariantMasters = async () => {
    try {
      const res = await gruhUdhyogVariantService.listPage({
        page: 1,
        page_size: 200,
        status: "active",
      });
      setVariantMasters(res.data ?? []);
    } catch (err) {
      console.error("Failed to load variant masters", err);
    }
  };

  useEffect(() => {
    void fetchProducts();
    void fetchCategories();
    void fetchVariantMasters();
  }, []);

  // Pagination State
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filter products client-side for immediate responsive experience
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      if (selectedCatId !== "All" && p.category !== selectedCatId) {
        return false;
      }
      if (search.trim()) {
        const s = search.toLowerCase();
        return (
          p.product_name.toLowerCase().includes(s) ??
          p.short_description?.toLowerCase().includes(s) ??
          p.category_name?.toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [products, selectedCatId, search]);

  // Reset to page 1 on filter or search change
  useEffect(() => {
    setPage(1);
  }, [selectedCatId, search]);

  const totalCount = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedProducts = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredProducts.slice(start, start + pageSize);
  }, [filteredProducts, page, pageSize]);

  const getCategoryName = (catId: number) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.category_name : "";
  };

  const hydrateVariantForm = (product: ProductFormState) => {
    setVariantRows(variantsFromProduct(product.variants));

    const masterId =
      product.variant_master ?? product.variant_master_details?.id ?? null;
    const master =
      variantMasters.find(m => m.id === masterId) ??
      (product.variant_master_details
        ? {
          id: product.variant_master_details.id,
          title: product.variant_master_details.title,
          values: product.variant_master_details.values ?? [],
          is_active: true,
        }
        : null);

    if (master) {
      const selectedFromVariants = (product.variants ?? [])
        .map(v => v.value ?? v.weight ?? "")
        .filter(Boolean);
      // Prefer single-part values that belong to this master
      const selectedValues = Array.from(
        new Set(
          selectedFromVariants.flatMap(v =>
            v.includes(" / ") ? v.split(" / ").map(s => s.trim()) : [v]
          )
        )
      ).filter(v => (master.values ?? []).includes(v));

      setProductOptions([
        {
          key: `opt-${master.id}`,
          masterId: master.id,
          title: master.title,
          availableValues: master.values ?? [],
          selectedValues: selectedValues.length > 0 ? selectedValues : [],
        },
      ]);
    } else {
      setProductOptions([]);
    }
  };

  const handleStartView = async (product: Product) => {
    setViewingProduct(product);
    setIsViewLoading(true);
    try {
      const full = await gruhUdhyogProductService.get(product.id);
      if (full) {
        setViewingProduct(full);
      }
    } catch (err) {
      projectToast.error(
        getApiErrorMessage(err, "Failed to load product details")
      );
    } finally {
      setIsViewLoading(false);
    }
  };

  const handleStartAdd = () => {
    setEditingProduct({
      is_active: true,
      is_handmade: false,
      is_signature: false,
    });
    setInitialProduct(null);
    setProductOptions([emptyOptionRow()]);
    setVariantRows([emptyVariantRow()]);
  };

  const handleStartEdit = async (product: Product) => {
    const base = {
      ...product,
      category: product.category ?? product.category_details?.id,
      category_name:
        product.category_name ?? product.category_details?.category_name,
    };
    const initialMasterId =
      base.variant_master ?? base.variant_master_details?.id;
    const initialVars = variantsFromProduct(base.variants);

    setEditingProduct(base);
    setInitialProduct({
      product: { ...base },
      variantMasterId: initialMasterId,
      variants: initialVars,
    });
    hydrateVariantForm(base);

    try {
      const full = await gruhUdhyogProductService.get(product.id);
      if (full) {
        const next = {
          ...full,
          category: full.category ?? full.category_details?.id,
          category_name:
            full.category_name ?? full.category_details?.category_name,
        };
        const fullMasterId =
          next.variant_master ?? next.variant_master_details?.id;
        const fullInitialVars = variantsFromProduct(next.variants);

        setEditingProduct(next);
        setInitialProduct({
          product: { ...next },
          variantMasterId: fullMasterId,
          variants: fullInitialVars,
        });
        hydrateVariantForm(next);
      }
    } catch (err) {
      console.error("Failed to load product details for editing:", err);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct) {
      return;
    }

    if (!editingProduct.product_name?.trim()) {
      return projectToast.error("Product name is required.");
    }
    if (!editingProduct.category) {
      return projectToast.error("Please select a category.");
    }

    const variantMasterId = resolveVariantMasterId(productOptions);
    if (!variantMasterId) {
      return projectToast.error(
        "Please add a product option and select a variant master."
      );
    }

    const variantsPayload = toVariantCreateItems(variantRows, {
      isEdit: Boolean(editingProduct.id),
    });
    if (variantsPayload.length === 0) {
      return projectToast.error(
        "Please select option values and fill at least one variant."
      );
    }

    const activeRows = variantRows.filter(r => r.value.trim());
    if (
      activeRows.some(
        r => r.price.trim() === "" || Number.isNaN(Number(r.price))
      )
    ) {
      return projectToast.error("Price is required for every variant.");
    }
    if (
      activeRows.some(
        r =>
          r.total_stock.trim() === "" ||
          Number.isNaN(Number(r.total_stock)) ||
          Number(r.total_stock) < 0
      )
    ) {
      return projectToast.error("Stock is required for every variant.");
    }
    if (
      activeRows.some(r => r.tax.trim() === "" || Number.isNaN(Number(r.tax)))
    ) {
      return projectToast.error("Tax (%) is required for every variant.");
    }

    setIsSaving(true);
    try {
      if (editingProduct.id) {
        // Build diff payload for editing - only pass changed values in FormData
        const updatePayload: Partial<ProductPayload> = {};
        const initial = initialProduct?.product;

        const currentName = editingProduct.product_name.trim();
        const initialName = initial?.product_name?.trim() ?? "";
        if (currentName !== initialName) {
          updatePayload.product_name = currentName;
        }

        if (editingProduct.category !== initial?.category) {
          updatePayload.category = editingProduct.category;
        }

        if (variantMasterId !== initialProduct?.variantMasterId) {
          updatePayload.variant_master = variantMasterId;
        }

        const currentShort = editingProduct.short_description?.trim() ?? "";
        const initialShort = initial?.short_description?.trim() ?? "";
        if (currentShort !== initialShort) {
          updatePayload.short_description = currentShort;
        }

        const currentDesc = editingProduct.description?.trim() ?? "";
        const initialDesc = initial?.description?.trim() ?? "";
        if (currentDesc !== initialDesc) {
          updatePayload.description = currentDesc;
        }

        const currentHandmade = Boolean(editingProduct.is_handmade);
        const initialHandmade = Boolean(initial?.is_handmade);
        if (currentHandmade !== initialHandmade) {
          updatePayload.is_handmade = currentHandmade;
        }

        const currentActive = Boolean(editingProduct.is_active);
        const initialActive = Boolean(initial?.is_active);
        if (currentActive !== initialActive) {
          updatePayload.is_active = currentActive;
        }

        const currentSignature = Boolean(editingProduct.is_signature);
        const initialSignature = Boolean(initial?.is_signature);
        if (currentSignature !== initialSignature) {
          updatePayload.is_signature = currentSignature;
        }

        if (editingProduct.image instanceof File) {
          updatePayload.image = editingProduct.image;
        }

        const variantsChanged = !areVariantsEqual(
          initialProduct?.variants ?? [],
          variantRows,
          initialProduct?.variantMasterId,
          variantMasterId
        );

        if (variantsChanged) {
          updatePayload.variants = variantsPayload;
        }

        if (Object.keys(updatePayload).length === 0) {
          projectToast.info("No changes were made.");
          setEditingProduct(null);
          setInitialProduct(null);
          return;
        }

        await gruhUdhyogProductService.update(editingProduct.id, updatePayload);
        projectToast.success("Product updated successfully.");
      } else {
        const payload: ProductPayload = {
          category: editingProduct.category,
          variant_master: variantMasterId,
          product_name: editingProduct.product_name.trim(),
          short_description: editingProduct.short_description?.trim() ?? "",
          description: editingProduct.description?.trim() ?? "",
          is_handmade: editingProduct.is_handmade === true,
          is_active: editingProduct.is_active === true,
          is_signature: editingProduct.is_signature === true,
          variants: variantsPayload,
          image: editingProduct.image,
        };

        await gruhUdhyogProductService.create(payload);
        projectToast.success("Product created successfully.");
      }
      setEditingProduct(null);
      setInitialProduct(null);
      void fetchProducts();
    } catch (err) {
      projectToast.error(getApiErrorMessage(err, "Failed to save product"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingProduct) {
      return;
    }
    setIsDeleting(true);
    try {
      await gruhUdhyogProductService.delete(deletingProduct.id);
      projectToast.success("Product deleted successfully.");
      setDeletingProduct(null);
      void fetchProducts();
    } catch (err) {
      projectToast.error(getApiErrorMessage(err, "Failed to delete product"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AdminShell
      title="Product Listing"
      subtitle="All Gruh Udhyog products on the storefront"
      actions={
        <Button
          onClick={handleStartAdd}
          size="sm"
          className="flex items-center gap-1.5 rounded-xl"
        >
          <Plus className="h-4 w-4" /> Add Product
        </Button>
      }
    >
      {/* Search & Filter Toolbar */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products..."
            className="bg-muted/40 rounded-xl border-transparent pl-9"
          />
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            size="sm"
            variant={selectedCatId === "All" ? "default" : "outline"}
            onClick={() => setSelectedCatId("All")}
            className="h-8 rounded-full px-4"
          >
            All
          </Button>
          {categories.filter(cat => cat.is_active).map(cat => (
            <Button
              key={cat.id}
              size="sm"
              variant={selectedCatId === cat.id ? "default" : "outline"}
              onClick={() => setSelectedCatId(cat.id)}
              className="h-8 rounded-full px-4"
            >
              {cat.category_name}
            </Button>
          ))}
          <div className="ml-2 text-xs text-muted-foreground">
            {filteredProducts.length} items
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        /* Cards Grid Layout */
        <div className="space-y-6">
          <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {paginatedProducts.map(p => {
              const priceRange = productPriceRange(p.variants);
              const totalStock = productTotalStock(p.variants);
              const variantCount = p.variants?.length ?? 0;
              const imageSrc = productImageSrc(p);
              const categoryName =
                p.category_name ?? getCategoryName(p.category);

              return (
                <div
                  key={p.id}
                  className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition hover:shadow-md"
                >
                  {/* Product Image */}
                  <div className="from-primary/5 relative flex aspect-[6/3] w-full items-center justify-center overflow-hidden border-b border-border bg-gradient-to-br via-amber-100/10 to-emerald-100/10">
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={p.product_name}
                        className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <Package className="text-primary/40 h-16 w-16" />
                    )}
                    {p.is_signature && (
                      <Badge className="absolute right-3 top-3 border-transparent bg-amber-500 text-[10px] font-semibold uppercase tracking-wider text-white hover:bg-amber-500">
                        Signature
                      </Badge>
                    )}

                    {p.is_handmade && (
                      <Badge className="absolute left-3 top-3 bg-emerald-600/90 text-white backdrop-blur-sm hover:bg-emerald-600/90">
                        Handmade
                      </Badge>
                    )}
                  </div>

                  {/* Product Body */}
                  <div className="flex flex-1 flex-col justify-between p-4">
                    <div className="space-y-1.5">
                      <h3 className="line-clamp-1 text-base font-bold text-foreground">
                        {p.product_name}
                      </h3>
                      <p className="line-clamp-2 min-h-[2rem] text-xs text-muted-foreground">
                        {p.short_description ??
                          p.description ??
                          "No description provided."}
                      </p>

                      {/* Category Badge below description */}
                      {categoryName && (
                        <div className="!mt-0">
                          <span className="inline-flex items-center rounded-md border border-border/80 bg-muted/60 px-2 py-0.5 text-[11px] font-medium text-foreground/80">
                            {categoryName}
                          </span>
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-[11px] text-muted-foreground">
                        {priceRange && (
                          <span className="font-semibold text-foreground">
                            {priceRange}
                          </span>
                        )}
                        <span>
                          {variantCount} variant{variantCount === 1 ? "" : "s"}
                        </span>
                        {totalStock != null && <span>Stock: {totalStock}</span>}
                      </div>
                    </div>

                    {/* Footer Info & Actions */}
                    <div className="border-border/60 mt-4 flex items-center justify-between border-t pt-3">
                      <Badge
                        variant="outline"
                        className={
                          p.is_active
                            ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300 font-medium"
                            : "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/50 dark:text-rose-300 font-medium"
                        }
                      >
                        {p.is_active ? "Active" : "Inactive"}
                      </Badge>

                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          onClick={() => void handleStartView(p)}
                          title="View Product"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          onClick={() => handleStartEdit(p)}
                          title="Edit Product"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => setDeletingProduct(p)}
                          title="Delete Product"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredProducts.length === 0 && (
              <div className="col-span-full py-16 text-center text-sm text-muted-foreground">
                No products found matching the criteria.
              </div>
            )}
          </div>

          {/* Admin Common Pagination */}
          {totalCount > 0 && (
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={size => {
                setPageSize(size);
                setPage(1);
              }}
              pageSizeOptions={[10, 20, 30, 50]}
              loading={loading}
            />
          )}
        </div>
      )}

      {/* Save Dialog */}
      <Dialog
        open={editingProduct !== null}
        onOpenChange={open => {
          if (!open) {
            setEditingProduct(null);
            setInitialProduct(null);
          }
        }}
      >
        <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
          <form
            onSubmit={handleSave}
            className="flex max-h-[90vh] flex-col overflow-hidden"
          >
            <DialogHeader className="shrink-0 border-b border-border px-6 py-4">
              <DialogTitle className="text-lg font-bold">
                {editingProduct?.id ? "Edit Product" : "Add Product"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5 overflow-y-auto px-6 py-4 text-sm">
              <div>
                <Label data-required="true">Product Name</Label>
                <Input
                  value={editingProduct?.product_name ?? ""}
                  onChange={e =>
                    setEditingProduct(prev => ({
                      ...(prev ?? {}),
                      product_name: e.target.value,
                    }))
                  }
                  placeholder="e.g. Methi Khakhra"
                  className="bg-muted/40 rounded-xl border-transparent"
                  required
                />
              </div>

              <div>
                <Label data-required="true">Category</Label>
                <SearchableSelect
                  value={
                    editingProduct?.category
                      ? String(editingProduct.category)
                      : ""
                  }
                  onValueChange={v =>
                    setEditingProduct(prev => ({
                      ...(prev ?? {}),
                      category: Number(v),
                      category_name: getCategoryName(Number(v)),
                    }))
                  }
                  options={categories.filter(cat => cat.is_active).map(cat => ({
                    value: String(cat.id),
                    label: cat.category_name,
                  }))}
                  placeholder="Select category..."
                  emptyText="No category found."
                  seedOption={
                    editingProduct?.category
                      ? {
                        value: String(editingProduct.category),
                        label:
                          editingProduct.category_name ??
                          getCategoryName(Number(editingProduct.category)),
                      }
                      : undefined
                  }
                />
              </div>

              <div>
                <Label>Short Description</Label>
                <Input
                  value={editingProduct?.short_description ?? ""}
                  onChange={e =>
                    setEditingProduct(prev => ({
                      ...(prev ?? {}),
                      short_description: e.target.value,
                    }))
                  }
                  placeholder="Brief summary..."
                  className="bg-muted/40 rounded-xl border-transparent"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingProduct?.description ?? ""}
                  onChange={e =>
                    setEditingProduct(prev => ({
                      ...(prev ?? {}),
                      description: e.target.value,
                    }))
                  }
                  placeholder="Detailed product explanation..."
                  rows={3}
                  className="bg-muted/40 rounded-xl border-transparent"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/40 flex h-11 items-center gap-3 rounded-xl border border-transparent px-3">
                  <Switch
                    checked={editingProduct?.is_handmade === true}
                    onCheckedChange={checked =>
                      setEditingProduct(prev => ({
                        ...(prev ?? {}),
                        is_handmade: checked,
                      }))
                    }
                  />
                  <span className="text-xs font-medium text-muted-foreground">
                    Handmade
                  </span>
                </div>

                <div className="bg-muted/40 flex h-11 items-center gap-3 rounded-xl border border-transparent px-3">
                  <Switch
                    checked={editingProduct?.is_signature === true}
                    onCheckedChange={checked =>
                      setEditingProduct(prev => ({
                        ...(prev ?? {}),
                        is_signature: checked,
                      }))
                    }
                  />
                  <span className="text-xs font-medium text-muted-foreground">
                    Signature
                  </span>
                </div>

                <div className="bg-muted/40 flex h-11 items-center gap-3 rounded-xl border border-transparent px-3">
                  <Switch
                    checked={editingProduct?.is_active === true}
                    onCheckedChange={checked =>
                      setEditingProduct(prev => ({
                        ...(prev ?? {}),
                        is_active: checked,
                      }))
                    }
                  />
                  <span className="text-xs font-medium text-muted-foreground">
                    {editingProduct?.is_active === true ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              <div>
                <Label>Product Image</Label>
                <div className="mt-2 flex items-center gap-3">
                  {(() => {
                    const previewSrc = productImageSrc(editingProduct);
                    return previewSrc ? (
                      <img
                        src={previewSrc}
                        alt="Preview"
                        className="h-16 w-16 rounded-xl border border-border object-cover"
                      />
                    ) : null;
                  })()}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={e => {
                      const file = e.target.files?.[0] ?? null;
                      if (file) {
                        setEditingProduct(prev => ({
                          ...(prev ?? {}),
                          image: file,
                        }));
                      }
                    }}
                    className="hover:file:bg-primary/90 bg-muted/40 h-10 rounded-xl border-transparent text-xs file:mr-2 file:rounded file:border-0 file:bg-primary file:px-2 file:py-0.5 file:text-[11px] file:font-semibold file:text-primary-foreground"
                  />
                </div>
              </div>

              <ProductFormVariants
                variantMasters={variantMasters}
                options={productOptions}
                onOptionsChange={setProductOptions}
                variants={variantRows}
                onVariantsChange={setVariantRows}
              />
            </div>

            <DialogFooter className="shrink-0 border-t border-border bg-background px-6 py-3 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditingProduct(null);
                  setInitialProduct(null);
                }}
                className="rounded-xl"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSaving}
                className="min-w-[70px] rounded-xl"
              >
                {isSaving ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <ProductViewDialog
        open={viewingProduct !== null}
        product={viewingProduct}
        loading={isViewLoading}
        onOpenChange={open => {
          if (!open) {
            setViewingProduct(null);
            setIsViewLoading(false);
          }
        }}
      />

      {/* Delete Dialog */}
      <DeleteConfirmDialog
        open={deletingProduct !== null}
        onOpenChange={open => {
          if (!open) {
            setDeletingProduct(null);
          }
        }}
        title="Delete product?"
        description="Are you sure you want to delete this product? All variants linked to it may become inaccessible."
        itemName={deletingProduct?.product_name}
        loading={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </AdminShell>
  );
}
